import { i18n } from '@lingui/core'
import { msg, t, Trans } from '@lingui/macro'
import Paper from '@material-ui/core/Paper'
import {
  Box,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { dateFormat } from 'app/appSettings'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import {
  getFieldMaxLength,
  getFieldScale
} from 'app/utils/salesforceFieldUtils'
import { ErrorLabel } from 'app/views/common-components/forms/ErrorLabel'
import { FormTopLabel } from 'app/views/common-components/forms/FormTopLabel'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { CurrencyFormated } from 'app/views/common/Formats'
import { FormHelptext } from 'app/views/forms/common/FormHelpText'
import currency from 'currency.js'
import { useField, useFormikContext } from 'formik'
import moment from 'moment'
import { useEffect } from 'react'
import { languages, myI18n } from 'translation/I18nConnectedProvider'
import * as Yup from 'yup'
import { parseFormLabelText } from '../../../common/Common'
import { requiredTrans } from '../../../formTranslations'
import { PRIMM_BUDGET_REQUESTED_KEY } from './FormBudgetUtils'

export const WHOLE_BUDGET = 'All time'

/**
 * Form element which renders a table containing project budget years, and expenses and revenues that can be filled for every budget year. Budget lines filled are saved as
 * Salesforce FGM_Portal__Grantee_Budget_Line_Item__c object, with connected Opportunity as parent.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {FormElementReference}  typeProps.requestedAmountId Deprecated. Reference to another element of Numeric Inpput type. The value of referenced elements will be used to set the target value of the whole budget.
 * @param  {FormElementReference}  typeProps.requestedAmountIds Required. Array of references to other elements of Numeric Inpput type. The values of referenced elements will be used to set the target value of the whole budget.
 * @param  {FormElementReference}  typeProps.milestonesId Required. Reference to another element of Milestones type. The value of referenced elements will be used to determine budget years.
 * @param  {Object[]} typeProps.revenuesLines Array of options defined in the form editor which will determine what budget lines for revenues section will be rendered.
 * @param  {Object[]} typeProps.expensesLines Array of options defined in the form editor which will determine what budget lines for expenses section will be rendered.
 * @param  {number}  [typeProps.commentsRows=1] Number of rows which comments text field should occupy.
 * @param  {boolean}  [typeProps.commentsExpandable=false] Defines if comments text field can expand further below if new line is inserted.
 * @param  {string}  [typeProps.revenuesHelpText] Help text that will be rendered above revenues section.
 * @param  {string}  [typeProps.expensesHelpText] Help text that will be rendered above expenses section.
 */
export const FormBudget = ({
  id,
  langVersion,
  useMultiuser,
  muBag,
  title,
  connectedObject,
  editMode,
  typeProps,
  i18n,
  elementsMap,
  disabledIds = [],
  describeMap,
  ...props
}) => {
  const amountMaxLengh = getFieldMaxLength(
    describeMap,
    'FGM_Portal__Grantee_Budget_Line_Item__c',
    'FGM_Portal__Amount__c'
  )
  const amountDecimalScale = getFieldScale(
    describeMap,
    'FGM_Portal__Grantee_Budget_Line_Item__c',
    'FGM_Portal__Amount__c'
  )
  let disabled = props.disabled
  let warning
  const { budgetCategories } = useGlobalQueries()
  const { values, setFieldValue } = useFormikContext()
  const [, meta] = useField(id)
  const currencyPlaceholder = langVersion !== 'fr' ? '$0' : '0 $'
  const error = meta.touched &&
    meta.error &&
    !meta.error.expenses &&
    !meta.error.revenues && <div>{meta.error}</div>
  const value = values[id] || {}
  const { revenues = {}, expenses = {} } = value
  const {
    requestedAmountId,
    revenuesLines = [],
    expensesLines = [],
    commentsRows,
    commentsExpandable,
    revenuesHelpText,
    expensesHelpText,
    expensesCommentsValidation
  } = typeProps

  let requestedAmountIds = typeProps.requestedAmountIds || []
  const budgetCentreGrantKey = budgetCentreGrantKeys.find((key) =>
    revenuesLines.includes(key)
  )

  // update requestedAmountIds if requestedAmountId is set and it is not in the list
  requestedAmountIds =
    !requestedAmountId ||
    (requestedAmountId && requestedAmountIds.includes(requestedAmountId))
      ? [...requestedAmountIds]
      : [...requestedAmountIds, requestedAmountId]

  const amountRequested = requestedAmountIds.reduce((acc, id) => {
    const value = Number(values[id] || 0)
    return acc + value
  }, 0)

  let projectStartDate, projectEndDate
  if (elementsMap) {
    Object.values(elementsMap).some((obj) => {
      if (obj.elementType === 'milestones' && !disabledIds.includes(obj.id)) {
        const mValue = values[obj.id]
        if (mValue && Array.isArray(mValue)) {
          const onlyUsed = mValue.filter((m) => m.isDisplayed)
          projectStartDate =
            onlyUsed[0]?.startDate &&
            moment.utc(onlyUsed[0].startDate).format(dateFormat)
          projectEndDate =
            onlyUsed[onlyUsed.length - 1]?.endDate &&
            moment.utc(onlyUsed[onlyUsed.length - 1].endDate).format(dateFormat)
        }
        return true
      }
      return false
    })
  }

  const invalid = Boolean(
    !connectedObject ||
    !connectedObject.Id ||
    connectedObject.attributes.type !== 'Opportunity'
  )

  // This useEffect hook is used to keep the "Transformation Centre Grant" budget line value
  // in sync with the sum of the values of the requested amount fields.
  useEffect(() => {
    const budgetCentreGrantValue =
      values?.revenues?.[budgetCentreGrantKey]?.value
    if (budgetCentreGrantValue !== amountRequested) {
      setFieldValue(
        id + `.revenues.${budgetCentreGrantKey}.value`,
        amountRequested
      )
    }
  }, [amountRequested])

  if (invalid && !editMode) {
    return (
      <div style={{ padding: 10, color: 'red' }}>
        {/* skip-fc */}
        <Trans>
          There is no object connected in editor or connected object is not of
          "Opportunity" type!
        </Trans>
      </div>
    )
  }
  if (requestedAmountIds.length === 0) {
    return (
      <div style={{ color: 'red' }}>
        <b>
          {/* skip-fc */}
          <Trans>
            Either Milestones or Requested Amount field were not connected in
            form editor!
          </Trans>
        </b>
      </div>
    )
  }

  if (!projectStartDate && !projectEndDate) {
    disabled = true
    warning = (
      <div style={{ margin: 20 }}>
        <Typography style={{ marginLeft: 25, fontSize: '21px', color: 'red' }}>
          <Trans>
            You need to set project dates in Milestones to edit the project
            Budget!
          </Trans>
        </Typography>
      </div>
    )
  }

  const mapping = budgetCategories.en_CA
  const revenuesMap = revenuesLines
    .map((line) => ({
      key: line,
      value: myI18n?._(line),
      order: mapping?.revenues?.[line]?.order
    }))
    .sort((a, b) => a.order - b.order)
  const expensesMap = expensesLines
    .map((line) => {
      return {
        key: sanitizeLineKey(line),
        value: myI18n?._(line),
        order: mapping?.expenses?.[line]?.order
      }
    })
    .sort((a, b) => a.order - b.order)

  const createTable = () => {
    const expensesHelpTextLabel = parseFormLabelText({
      text: expensesHelpText,
      langVersion,
      i18n
    })
    const revenuesHelpTextLabel = parseFormLabelText({
      text: revenuesHelpText,
      langVersion,
      i18n
    })

    const revenueSum = revenuesMap
      .reduce((acc, item) => {
        if (!revenues?.[item?.key]) {
          return acc
        }
        return [
          ...acc,
          {
            ...revenues?.[item?.key],
            key: item.key
          }
        ]
      }, [])
      .reduce((acc, item) => acc.add(item.value), currency(0))

    const expenseSum = expensesMap
      .reduce((acc, item) => {
        if (!expenses?.[item?.key]) {
          return acc
        }
        return [
          ...acc,
          {
            ...expenses?.[item?.key],
            key: item.key
          }
        ]
      }, [])
      .reduce((acc, item) => acc.add(item.value), currency(0))

    return (
      <Box display='flex' flexDirection='column' gap={2}>
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            whiteSpace: 'pre',
            backgroundColor: '',
            border: '1px solid #17374F',
            borderCollapse: 'collapse',
            borderSpacing: 0
          }}
        >
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '50%' }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                colSpan={3}
                sx={{
                  padding: '24px 40px'
                }}
              >
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '25px'
                  }}
                >
                  <Trans>Budget</Trans>
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ padding: '24px 0px 24px 40px' }} colSpan={3}>
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '25px'
                  }}
                >
                  <Trans>Revenues</Trans>
                </Typography>
                <FormHelptext helpText={revenuesHelpTextLabel} />
              </TableCell>
            </TableRow>
            {revenuesMap.map((item, index) => {
              const line = value.revenues?.[item.key] || {
                value: 0,
                comment: ''
              }
              if (!line.value) {
                line.value = 0
              }
              const nonEditable =
                item.key === budgetCentreGrantKey &&
                requestedAmountIds.length > 0

              return (
                <TableRow key={index}>
                  <TableCell style={{ paddingLeft: 40 }}>
                    <Typography
                      sx={{
                        color: '#000',
                        fontFamily: 'Open Sans',
                        fontSize: '16px',
                        fontWeight: '600',
                        lineHeight: '25px'
                      }}
                    >
                      <Trans id={item.value} />
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', paddingRight: 2 }}>
                    {nonEditable
                      ? (
                        <div style={{ padding: 6 }}>
                          <CurrencyFormated
                            value={line.value}
                            maximumFractionDigits={2}
                          />
                        </div>
                        )
                      : (
                        <>
                          <FormTopLabel>
                            <Trans>Amount</Trans> *
                          </FormTopLabel>
                          <TextFieldWithFormik
                            name={id + `.revenues[${item.key}].value`}
                            elementType='textInputNumeric'
                            type='currency'
                            placeholder={langVersion !== 'fr' ? '$0' : '0 $'}
                            decimalScale={amountDecimalScale}
                            limit={amountMaxLengh}
                            disabled={
                            disabled ||
                            invalid ||
                            item.key === budgetCentreGrantKey
                          }
                            sx={{
                              '& .MuiInputBase-root.Mui-disabled': {
                                backgroundColor: 'rgba(0, 0, 0, 0.05)'
                              },
                              '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                              {
                                borderColor: 'rgba(0, 0, 0, 0.4)'
                              }
                            }}
                          />
                        </>
                        )}
                  </TableCell>

                  <TableCell
                    sx={{ verticalAlign: 'top', paddingRight: '40px' }}
                  >
                    <FormTopLabel>
                      <Trans>Comments</Trans>{line.value > 0 && ' *'}
                    </FormTopLabel>
                    <TextFieldWithFormik
                      required={line.value > 0}
                      name={id + `.revenues[${item.key}].comment`}
                      multiline
                      minRows={1}
                      maxRows={commentsExpandable ? null : commentsRows || 1}
                      disabled={disabled || invalid}
                      sx={{
                        '& .MuiInputBase-root.Mui-disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        },
                        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: 'rgba(0, 0, 0, 0.4)'
                          }
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
          <TableFooter>
            <TableRow
              sx={{
                backgroundColor: disabled ? 'rgba(0, 0, 0, 0.05)' : '#EEEEE6'
              }}
            >
              <TableCell
                sx={{
                  borderBottom: '1px solid #17374F',
                  padding: '16px 0px 24px 40px'
                }}
              >
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '25px'
                  }}
                >
                  <Trans>SUM_OF_REVENUES</Trans>
                </Typography>
              </TableCell>
              <TableCell
                colSpan={2}
                sx={{
                  borderBottom: '1px solid #17374F'
                }}
              >
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '25px'
                  }}
                >
                  <CurrencyFormated
                    value={revenueSum.value}
                    maximumFractionDigits={2}
                  />
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
        <Table
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            whiteSpace: 'pre',
            backgroundColor: '',
            border: '1px solid #17374F',
            borderCollapse: 'collapse',
            borderSpacing: 0
          }}
        >
          <colgroup>
            <col style={{ width: '25%' }} />
            <col style={{ width: '25%' }} />
            <col style={{ width: '50%' }} />
          </colgroup>
          <TableBody>
            <TableRow>
              <TableCell sx={{ padding: '24px 0px 24px 40px' }} colSpan={3}>
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '25px'
                  }}
                >
                  <Trans>Expenses</Trans>
                </Typography>
                <FormHelptext helpText={expensesHelpTextLabel} />
              </TableCell>
            </TableRow>
            {expensesMap.map((item, index) => {
              const expenseValue = value?.expenses?.[item?.key]?.value
              return (
                <TableRow key={index}>
                  <TableCell style={{ paddingLeft: '40px' }}>
                    <Typography
                      sx={{
                        color: '#000',
                        fontFamily: 'Open Sans',
                        fontSize: '16px',
                        fontWeight: '600',
                        lineHeight: '25px'
                      }}
                    >
                      <Trans id={item.value} />
                    </Typography>
                  </TableCell>
                  <TableCell style={{ verticalAlign: 'top', paddingRight: 2 }}>
                    <FormTopLabel>
                      <Trans>Amount</Trans> *
                    </FormTopLabel>
                    <TextFieldWithFormik
                      // skip-fc
                      name={id + `.expenses[${item.key}].value`}
                      type='currency'
                      decimalScale={amountDecimalScale}
                      limit={amountMaxLengh}
                      placeholder={langVersion !== 'en' ? '0 $' : '$0'}
                      disabled={disabled || invalid}
                      sx={{
                        '& .MuiInputBase-root.Mui-disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        },
                        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: 'rgba(0, 0, 0, 0.4)'
                          }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', paddingRight: '40px' }}>
                    <FormTopLabel><Trans>Comments</Trans>{(expenseValue > 0) && ' *'}</FormTopLabel>
                    <TextFieldWithFormik
                      name={id + `.expenses[${item.key}].comment`}
                      multiline
                      required={expensesCommentsValidation && expenseValue > 0}
                      minRows={1}
                      maxRows={commentsExpandable ? null : commentsRows || 1}
                      disabled={disabled || invalid}
                      sx={{
                        '& .MuiInputBase-root.Mui-disabled': {
                          backgroundColor: 'rgba(0, 0, 0, 0.05)'
                        },
                        '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline':
                          {
                            borderColor: 'rgba(0, 0, 0, 0.4)'
                          }
                      }}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
          <TableFooter>
            <TableRow
              sx={{
                backgroundColor: disabled ? 'rgba(0, 0, 0, 0.05)' : '#EEEEE6'
              }}
            >
              <TableCell
                sx={{
                  borderBottom: '1px solid #17374F',
                  padding: '16px 0px 24px 40px'
                }}
              >
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '25px'
                  }}
                >
                  <Trans>Sum of expenses</Trans>
                </Typography>
              </TableCell>
              <TableCell colSpan={2} sx={{ borderBottom: '1px solid #17374F' }}>
                <Typography
                  sx={{
                    color: '#000',
                    fontFamily: 'Open Sans',
                    fontSize: '18px',
                    fontWeight: '700',
                    lineHeight: '25px'
                  }}
                >
                  <CurrencyFormated
                    value={expenseSum.value}
                    maximumFractionDigits={2}
                  />
                </Typography>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
        <Box
          sx={{
            display: 'flex',
            padding: '16px 0',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            backgroundColor: disabled ? 'rgba(23, 55, 79, 0.30)' : '#17374F',
            borderRadius: 0
          }}
        >
          <Typography
            sx={{
              color: '#FFF',
              textAlign: 'center',
              fontFmily: 'Open Sans',
              fontSize: '18px',
              fontWeight: '700',
              lineHeight: '25px'
            }}
          >
            <Trans>Total of revenue - expenses</Trans>
          </Typography>
          <Typography
            sx={{
              color: '#FFF',
              textAlign: 'center',
              fontFmily: 'Open Sans',
              fontSize: '18px',
              fontWeight: '700',
              lineHeight: '25px'
            }}
          >
            <CurrencyFormated
              value={revenueSum.subtract(expenseSum)}
              maximumFractionDigits={2}
            />
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <div>
      <h3>{title}</h3>
      {warning}
      <div className='w-100 overflow-auto'>
        <TableContainer
          sx={{
            backgroundColor: '#fff'
          }}
        >
          {createTable()}
        </TableContainer>
      </div>
      {error && (
        <div style={{ marginTop: 16 }}>
          <ErrorLabel label={error} />
        </div>
      )}
    </div>
  )
}

export const formBudgetParseValueToCompare = (v) => {
  const toRet = { ...v }
  return toRet
}

export const formBudgetValueToText = (v) => {
  const toRet = languages.reduce((acc, lang) => {
    acc[lang] = <Trans>Data is too complex to show differences!</Trans>
    return acc
  }, {})
  return toRet
}

export const budgetCentreGrantKeys = ['Transformation Centre Grant', PRIMM_BUDGET_REQUESTED_KEY]

export const sanitizeLineKey = (key) => {
  if (key === 'Indirect costs (Rental space, Insurance, Maintenance, etc...)') {
    return 'Indirect costs (Rental space, Insurance, Maintenance, etc)'
  } else {
    return key
  }
}

const parseLineKeySF = (key) => {
  if (key === 'Indirect costs (Rental space, Insurance, Maintenance, etc)') {
    return 'Indirect costs (Rental space, Insurance, Maintenance, etc...)'
  } else {
    return key
  }
}

Yup.addMethod(
  Yup.object,
  'checkBudgetValidity',
  function (revenuesLines, expensesLines) {
    return this.test('checkBudgetValidity', requiredTrans, function (object) {
      const sum = calcBudgetTotal(object, revenuesLines, expensesLines)
      const { path, createError } = this
      if (sum.value !== 0) {
        return createError({
          path,
          message: (
            <Trans>Budget expenses are not equal to budget revenues</Trans>
          )
        })
      }
      return true
    })
  }
)

Yup.addMethod(
  Yup.object,
  'checkAmountRequestedValidity',
  function (ids, revenuesLines) {
    return this.test(
      'checkAmountRequestedValidity',
      requiredTrans,
      function (object) {
        if (ids.length === 0) {
          return true
        }
        const { path, createError, parent } = this
        const requestedAmount = ids.reduce((acc, id) => {
          const value = Number(parent[id] || 0)
          return acc + value
        }, 0)

        const usedKey = budgetCentreGrantKeys.find(key => Object.keys(object?.revenues).some(revenueKey => revenueKey === key))
        const budgetCentreGrantValue = object?.revenues?.[usedKey]?.value

        if (+budgetCentreGrantValue !== +requestedAmount) {
          if (usedKey === PRIMM_BUDGET_REQUESTED_KEY) {
            return createError({
              path,
              message: (
                <Trans>
                  PRRIMM_AMOUNT_REQUESTED_MISSMATCH_ERROR
                </Trans>
              )
            })
          } else {
            return createError({
              path,
              message: (
                <Trans>
                  Amount requested and centre's grant in budget do not match!
                </Trans>
              )
            })
          }
        }
        return true
      }
    )
  }
)

Yup.addMethod(
  Yup.object,
  'checkExpensesRevenuesLinesValidity',
  function (validLines = []) {
    return this.test(
      'checkExpensesRevenuesLinesValidity',
      requiredTrans,
      function (object) {
        const { path } = this
        const errors = validLines
          .reduce((acc, item) => {
            const sanitizedKey = sanitizeLineKey(item)
            return [
              ...acc,
              {
                ...object?.[sanitizedKey],
                key: sanitizedKey
              }
            ]
          }, [])
          .reduce((acc, item) => {
            const value = Number(item.value) || 0
            if (value && !item.comment) {
              return [
                ...acc,
                new Yup.ValidationError(
                  requiredTrans,
                  item.comment,
                  path + `[${item.key}].comment`
                )
              ]
            }
            return acc
          }, [])

        if (errors.length === 0) {
          return true
        }
        return new Yup.ValidationError(errors)
      }
    )
  }
)

export const formBudgetValidation = (item, options) => {
  const schemaObj = {}
  const {
    expensesMustMatchRevenues,
    expensesCommentsValidation,
    requestedAmountIds = [],
    expensesLines = [],
    revenuesLines = []
  } = item.typeProps

  const describeMap = options?.describeMap || {}

  const amountMaxLengh = getFieldMaxLength(
    describeMap,
    'FGM_Portal__Grantee_Budget_Line_Item__c',
    'FGM_Portal__Amount__c'
  )

  const createLineSchema = (lines) => {
    const lineSchema = {}
    lines.forEach((line) => {
      const sanitizedKey = sanitizeLineKey(line)
      lineSchema[sanitizedKey] = Yup.object({
        value: Number.isFinite(amountMaxLengh)
          ? Yup.string().max(
            amountMaxLengh,
            t`This fields length cannot exceed ${amountMaxLengh} characters!`
          )
          : Yup.string()
      })
    })
    return Yup.object(lineSchema)
  }

  if (expensesCommentsValidation) {
    const validLines = expensesLines || []
    schemaObj.expenses = createLineSchema(
      validLines
    ).checkExpensesRevenuesLinesValidity(
      validLines.map((line) => sanitizeLineKey(line))
    )
  }

  const validLines = revenuesLines || []
  schemaObj.revenues = createLineSchema(
    validLines
  ).checkExpensesRevenuesLinesValidity(
    validLines.map((line) => sanitizeLineKey(line))
  )

  let toReturn = Yup.object(schemaObj).checkAmountRequestedValidity(
    requestedAmountIds,
    revenuesLines
  )
  if (expensesMustMatchRevenues) {
    toReturn = toReturn.checkBudgetValidity(revenuesLines, expensesLines)
  }
  return toReturn
}

export const FormBudgetExtractKey = ({
  saveMap,
  value,
  connectedObjectId,
  budgetCategories
}) => {
  const inner = []
  const mapping = budgetCategories.en_CA
  for (const category in value.revenues) {
    inner.push({
      FGM_Portal__Amount__c: value.revenues[category].value || 0,
      FGM_Portal__Note__c: value.revenues[category].comment,
      Id: value.revenues[category].id,
      FGM_Portal__Category__c: mapping.revenues[category].id,
      FGM_Portal__Grantee_Budget__r: {
        Name: WHOLE_BUDGET
      }
    })
  }

  for (const category in value.expenses) {
    inner.push({
      FGM_Portal__Amount__c: value.expenses[category].value || 0,
      FGM_Portal__Note__c: value.expenses[category].comment,
      Id: value.expenses[category].id,
      FGM_Portal__Category__c: mapping.expenses[parseLineKeySF(category)].id,
      FGM_Portal__Grantee_Budget__r: {
        Name: WHOLE_BUDGET
      }
    })
  }

  saveMap[connectedObjectId].FGM_Portal__Grantee_Budget_Line_Items__r = inner
}

const calcBudgetTotal = (obj, revenuesLines, expensesLines) => {
  let sum = 0
  if (!revenuesLines || !expensesLines) {
    return sum
  }
  const revenueSum = revenuesLines
    .reduce((acc, item) => {
      if (!obj?.revenues?.[item]) {
        return acc
      }
      return [
        ...acc,
        {
          ...obj?.revenues?.[item],
          key: item
        }
      ]
    }, [])
    .reduce((acc, item) => {
      return acc.add(item.value || 0)
    }, currency(0))

  const expenseSum = expensesLines
    .reduce((acc, item) => {
      const sanitizedKey = sanitizeLineKey(item)
      if (!obj?.expenses?.[sanitizedKey]) {
        return acc
      }
      return [
        ...acc,
        {
          ...obj?.expenses?.[sanitizedKey],
          key: sanitizedKey
        }
      ]
    }, [])
    .reduce((acc, item) => acc.add(item.value || 0), currency(0))
  sum = revenueSum.subtract(expenseSum)
  return sum
}

export const formBudgetDefaultValue = (obj) => {
  const ret = {
    revenues: {},
    expenses: {}
  }
  if (!obj || !obj.FGM_Portal__Grantee_Budget_Line_Items__r) {
    return ret
  }

  const wholeBudgetLines =
    obj.FGM_Portal__Grantee_Budget_Line_Items__r.records.filter(
      (line) => line.FGM_Portal__Grantee_Budget__r.Name === WHOLE_BUDGET
    )

  for (const line of wholeBudgetLines) {
    if (!line.FGM_Portal__Category__r) {
      continue
    }
    const portalCategoryName = sanitizeLineKey(
      line.FGM_Portal__Category__r.Name
    )
    const portalParentCategoryName =
      line.FGM_Portal__Category__r.FGM_Portal__Parent_Category__r.Name

    const toSet = {
      value: line.FGM_Portal__Amount__c,
      id: line.Id,
      comment: line.FGM_Portal__Note__c || '',
      modifiedDate: line.LastModifiedDate,
      order: line.FGM_Portal__Category__r.Order__c
    }
    const retByPortalParentCategoryNameConfig = {
      Revenue: ret.revenues,
      Expenses: ret.expenses
    }
    const retByPortalParentCategoryName =
      retByPortalParentCategoryNameConfig[portalParentCategoryName]
    if (retByPortalParentCategoryName) {
      retByPortalParentCategoryName[portalCategoryName] = toSet
    }
  }

  return ret
}

export const Budget = ({
  year,
  revenuesRender,
  expensesRender,
  editorPreview
}) => {
  return (
    <div key={year}>
      <div style={{ padding: 8, textAlign: 'center' }}>
        <Typography className='form-print-subtitle'>
          <Trans>Year</Trans>
        </Typography>
        <Typography style={{ fontSize: 21 }}>{year}</Typography>
      </div>

      {/* <div
        className='form-print-subtitle-big'
        style={{
          marginTop: 20,
          pageB reakBefore: 'always',
          breakBefore: 'always'
        }}
      >
        <Trans>Revenues</Trans>
      </div> */}
      <Paper elevation={6} style={{ marginTop: 10, padding: 10 }}>
        <Grid container>
          <Grid item style={{ padding: 4, width: 300 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Revenue</Trans>
            </Typography>
          </Grid>
          <Grid item style={{ padding: 4, width: 170 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Value</Trans>
            </Typography>
          </Grid>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Comment</Trans>
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        {revenuesRender.map((obj, index) => {
          return (
            <Grid container key={index}>
              <Grid item style={{ padding: 4, width: 300 }}>
                <Typography style={{ whiteSpace: 'pre-line' }}>
                  <Trans id={obj.key} />
                </Typography>
              </Grid>
              <Grid item style={{ padding: 4, width: 170 }}>
                <Typography style={{ whiteSpace: 'pre-line' }}>
                  {obj.value !== '$ Value Placeholder'
                    ? (
                      <CurrencyFormated
                        value={obj.value}
                        maximumFractionDigits={2}
                      />
                      )
                    : (
                      <Trans>$ Value Placeholder</Trans>
                      )}
                </Typography>
              </Grid>
              <Grid item xs style={{ padding: 4 }}>
                <Typography style={{ minHeight: 21 }}>{obj.comment}</Typography>
              </Grid>
            </Grid>
          )
        })}
      </Paper>

      {/* <div
        className='form-print-subtitle-big'
        style={{
          marginTop: 20,
          pageBreakBefore: 'always',
          breakBefore: 'always'
        }}
      >
        <Trans>Expenses</Trans>
      </div> */}
      <Paper
        elevation={6}
        style={{ marginTop: 10, padding: 10 }}
        className='avoid-print-break'
      >
        <Grid container>
          <Grid item style={{ padding: 4, width: 300 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Expense</Trans>
            </Typography>
          </Grid>
          <Grid item style={{ padding: 4, width: 170 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Value</Trans>
            </Typography>
          </Grid>
          <Grid item xs style={{ padding: 4 }}>
            <Typography className='form-print-subtitle'>
              <Trans>Comment</Trans>
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        {expensesRender.map((obj, index) => {
          return (
            <Grid container key={index}>
              <Grid item style={{ padding: 4, width: 300 }}>
                <Typography style={{ whiteSpace: 'pre-line' }}>
                  <Trans id={obj.key} />
                </Typography>
              </Grid>
              <Grid item style={{ padding: 4, width: 170 }}>
                <Typography style={{ whiteSpace: 'pre-line' }}>
                  {obj.value !== '$ Value Placeholder'
                    ? (
                      <CurrencyFormated
                        value={obj.value}
                        maximumFractionDigits={2}
                      />
                      )
                    : (
                      <Trans>$ Value Placeholder</Trans>
                      )}
                </Typography>
              </Grid>
              <Grid item xs style={{ padding: 4 }}>
                <Typography style={{ minHeight: 21 }}>{obj.comment}</Typography>
              </Grid>
            </Grid>
          )
        })}
      </Paper>
    </div>
  )
}

export const budgetTranslations = {
  'Transformation Centre Grant': msg`Transformation Centre Grant`,
  'Other Grants': msg`Other Grants`,
  'Fundraising/Donations': msg`Fundraising/Donations`,
  'Internal Revenue': msg`Internal Revenue`,
  'Personnel - Internal Wages and Benefits': msg`Personnel - Internal Wages and Benefits`,
  'Personnel - External Professional/consultant': msg`Personnel - External Professional/consultant`,
  'Direct costs (travel, materials, equipment, supplies, licensing, training costs)': msg`Direct costs (travel, materials, equipment, supplies, licensing, training costs)`,
  'Indirect costs (Rental space, Insurance, Maintenance, etc)': msg`Indirect costs (Rental space, Insurance, Maintenance, etc)`,
  'Administrative costs': msg`Administrative costs`,
  Other: msg`Other`
}

export const formBudgetValueToExportText = (value, _) => {
  if (!value) {
    return ''
  }

  const { expenses = {}, revenues = {} } = value

  const revenuesString = Object.entries(revenues)
    .map(([categoryKey, categoryData]) => {
      const categoryTranslation = budgetTranslations[categoryKey]
      const categoryString = categoryTranslation
        ? i18n._(categoryTranslation)
        : categoryKey || ''

      const amountString = `${i18n._(t`Amount`)}: $${categoryData?.value !== undefined ? categoryData.value : ''}`
      const commentString = categoryData?.comment
        ? `${i18n._(t`Comment`)}: ${categoryData.comment}\n`
        : ''

      return `${categoryString}:\n${amountString}\n${commentString}`
    })
    .join('\n')

  const expensesString = Object.entries(expenses)
    .map(([categoryKey, categoryData]) => {
      const categoryTranslation = budgetTranslations[categoryKey]
      const categoryString = categoryTranslation
        ? i18n._(categoryTranslation)
        : categoryKey || ''

      const amountString = `${i18n._(t`Amount`)}: $${categoryData?.value !== undefined ? categoryData.value : ''}`
      const commentString = categoryData?.comment
        ? `${i18n._(t`Comment`)}: ${categoryData.comment}\n`
        : ''

      return `${categoryString}:\n${amountString}\n${commentString}`
    })
    .join('\n')

  return `${i18n._(t`Revenues`)}\n${revenuesString}\n\n${i18n._(t`Expenses`)}\n${expensesString}`
}
